import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Loadable, loadWith } from '@dg/shared-rxjs';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';

import { Location } from '@angular/common';
import { RequiredLearningCounts } from '@app/notifications/notification-api.model';
import { AuthUser } from '@app/account/account-api.model';

import { NotificationService } from '@app/notifications/notification.service';
import { OpportunityPermissionsService } from '@app/opportunities/services/opportunity-permissions.service';
import { AuthService } from '@app/shared/services/auth.service';
import { TranslateService } from '@ngx-translate/core';
import { OpportunityFlagsService } from '@app/opportunities/services/opportunity-flags.service';
import { LearnerHomeTrackerService } from '@app/learner-home/services/learner-home-tracker.service';
import { take } from 'rxjs/operators';
import { UserInputsService } from '@app/inputs/services/user-inputs.service';
import { ProfileFlagsService } from '@app/profile/services/profile-flags.service';
import { LDFlagsService } from '@dg/shared-services';

interface Resource {
  /** Title to display */
  label: string;
  /** Link for this resource */
  link: string;
  /** If the resource needs to use a custom template */
  customTemplate?: TemplateRef<any>;
  /** When using the custom template, this context will be passed to the template */
  context?: any;
}

interface RequiredLearningCountsDisplay extends RequiredLearningCounts {
  recommendedLearningDueLater: number;
}

@Component({
  selector: 'dgx-learner-home-resources',
  templateUrl: './learner-home-resources.component.html',
  styleUrls: ['./learner-home-resources.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LearnerHomeResourcesComponent
  extends SubscriberBaseDirective
  implements OnInit, Loadable
{
  public selectedPage: string = 'discover';
  public assignmentsCountAriaLabel: string;
  public assignmentsCounts: RequiredLearningCountsDisplay;
  public resources: Resource[];
  public isLoading: boolean = true;
  @ViewChild('assignmentsBadge', { static: true })
  public assignmentsBadgeTemplate: TemplateRef<any>;
  public i18n = this.translateService.instant([
    'DashboardNav_Resources',
    'DashboardNav_Assignments',
    'dgAssignmentsOverview_Overdue',
    'dgAssignmentsOverview_DueSoon',
    'dgAssignmentsOverview_Incomplete',
    'Core_Shared',
    'Core_Saved',
    'Core_Pathways',
    'Core_Plans',
    'Core_Groups',
    'Core_Opportunities',
    'Core_Discover',
    'SkillStd_SkillStandards',
  ]);

  public useNewAssignments =
    this.profileFlagsService.showProfileAssignmentsList;

  public skillStandardsLearnerViewLdFlag =
    this.ldFlagService.skills.skillStandardsLearnerView;

  constructor(
    private translateService: TranslateService,
    private authService: AuthService,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
    private opportunityPermissionsService: OpportunityPermissionsService,
    private opportunityFlagsService: OpportunityFlagsService,
    private learnerHomeTrackerService: LearnerHomeTrackerService,
    private location: Location,
    private userInputsService: UserInputsService,
    private profileFlagsService: ProfileFlagsService,
    private ldFlagService: LDFlagsService
  ) {
    super();
  }

  public ngOnInit(): void {
    // get the name of the displayed tab from the URL when this is loaded, so we can set the active state on the correct menu option
    // this is just the last part of the path
    this.selectedPage = this.location
      .path()
      .split('/')
      .filter((e) => e)
      .slice(-1)[0];

    this.setResources();

    this.userInputsService.inputsChange
      .pipe(this.takeUntilDestroyed())
      .subscribe(() => {
        this.loadCounts();
      });
    this.loadCounts();
  }

  public setResources() {
    const profileUrl: string =
      this.authService.authUser.viewerProfile.profileUrl;
    const authUser: AuthUser = this.authService.authUser;
    const authUserUrl: string = `/profile/${profileUrl}/`;
    const canViewOpportunities: boolean =
      this.opportunityPermissionsService.canViewOpportunities(authUser);
    this.resources = [
      {
        label: this.i18n.Core_Saved,
        link: 'saved',
      },
      {
        label: this.i18n.Core_Shared,
        link: 'shared',
      },
      {
        label: this.i18n.Core_Pathways,
        link: 'pathways',
      },
    ];

    if (authUser.defaultOrgInfo?.settings?.supportTargets) {
      this.resources.push({
        label: this.i18n.Core_Plans,
        link: 'plans',
      });
    }

    if (!!authUser.defaultOrgInfo) {
      this.resources.push({
        label: this.i18n.Core_Groups,
        link: 'groups',
      });
    }

    if (this.skillStandardsLearnerViewLdFlag) {
      this.resources.push({
        label: this.i18n.SkillStd_SkillStandards,
        link: 'skillstandards',
      });
    }

    if (!authUser.defaultOrgInfo?.settings?.disableAssignedLearning) {
      this.resources.unshift({
        label: this.i18n.DashboardNav_Assignments,
        link: this.useNewAssignments ? 'assignments-v2' : 'assignments',
        customTemplate: this.assignmentsBadgeTemplate,
      });
    }

    this.resources = [
      {
        label: this.i18n.Core_Discover,
        link: 'discover',
        // @TODO remove this when all tabs are routerLinks
      },
      ...this.resources,
    ];
  }

  public getAssignmentsBadgeColor(counts: RequiredLearningCountsDisplay) {
    if (counts.recommendedLearningOverdue > 0) {
      return 'badge-danger';
    } else if (counts.recommendedLearningDueSoon > 0) {
      return 'badge-attention';
    } else if (counts.recommendedLearningTotal > 0) {
      return 'badge-emphasize';
    } else {
      return 'badge-neutral';
    }
  }

  public trackClick(itemClicked: string, linkName: string) {
    this.selectedPage = linkName;
    const isAssignment = itemClicked === 'Assignments';
    this.learnerHomeTrackerService.navigationClicked({
      context: 'Sidebar',
      itemClicked,
      totalAssignments: isAssignment
        ? this.assignmentsCounts.recommendedLearningTotal
        : undefined,
      overdueAssignments: isAssignment
        ? this.assignmentsCounts.recommendedLearningOverdue
        : undefined,
    });
  }

  private loadCounts() {
    loadWith(this.notificationService.loadShallowCounts(), this)
      .pipe(take(1))
      .subscribe((data) => {
        this.assignmentsCounts = {
          ...data,
          recommendedLearningDueLater: this.getDueLaterCount(data),
        };
        this.cdr.markForCheck();
        this.assignmentsCountAriaLabel = this.translateService.instant(
          'A11y_AssignmentsCount',
          {
            n: this.assignmentsCounts.recommendedLearningTotal,
          }
        );
      });
  }
  private getDueLaterCount(counts: RequiredLearningCounts) {
    return (
      counts.recommendedLearningTotal -
      counts.recommendedLearningOverdue -
      counts.recommendedLearningDueSoon
    );
  }
}
